// Tab "Solicitudes de cotizaciones" — paso previo a una cotización formal.
import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { MdCheck, MdClose } from "react-icons/md";
import { UserContext } from "../contexts/UserContext";
import CotizacionesNav from "./CotizacionesNav";

const BASE = process.env.REACT_APP_API_BASE || "http://localhost/twinpack/php";

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatFecha = (fechaStr) => {
  if (!fechaStr || fechaStr === "0000-00-00 00:00:00") return "—";
  const d = new Date(fechaStr);
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const diasColor = (dias) => {
  if (dias == null) return { bg: "#f5f5f5", color: "#999" };
  if (dias <= 3)    return { bg: "#d4f5e2", color: "#0a6e3f" };
  if (dias <= 7)    return { bg: "#fff3cd", color: "#856404" };
  return { bg: "#fde8e8", color: "#a00" };
};

// ─── Estilos ─────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "#f7f4f6",
    padding: "32px 16px",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
  },
  header: {
    maxWidth: 1100,
    margin: "0 auto 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  title:    { fontSize: 24, fontWeight: 700, color: "#222", margin: 0 },
  subtitle: { fontSize: 13, color: "#888", marginTop: 4 },
  btnPrimary: {
    background: "#9f0059", color: "#fff", border: "none",
    padding: "10px 22px", borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: "pointer",
  },
  card: {
    maxWidth: 1100, margin: "0 auto", background: "#fff",
    borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.04)", overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    padding: "12px 14px", textAlign: "left", background: "#fafafa",
    fontSize: 12, fontWeight: 700, color: "#666", textTransform: "uppercase",
    letterSpacing: 0.4, borderBottom: "1.5px solid #ede8ed",
  },
  td: { padding: "13px 14px", borderBottom: "1px solid #f5f0f2", fontSize: 13, color: "#333" },
  trOdd:  { background: "#fff" },
  trEven: { background: "#fdfaf7" },
  emptyRow: {
    textAlign: "center", padding: 40, color: "#aaa", fontSize: 14,
    borderBottom: "none",
  },
  diasBadge: (dias) => {
    const c = diasColor(dias);
    return {
      display: "inline-block", padding: "3px 10px", borderRadius: 12,
      fontSize: 11, fontWeight: 700, background: c.bg, color: c.color,
    };
  },
  etapaChip: (estado) => ({
    display: "inline-block", padding: "3px 10px", borderRadius: 12,
    fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", cursor: "default",
    background: estado === "en_fabrica" ? "#e0f0ff" : "#f0f0f0",
    color:      estado === "en_fabrica" ? "#1a5fa8" : "#888",
  }),
  accionesCel: { display: "flex", gap: 6 },
  iconBtn: (color, title) => ({
    background: "none", border: `1.5px solid ${color}30`,
    color, cursor: "pointer", padding: 6, borderRadius: 7,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "all 0.15s", fontSize: 15,
    title,
  }),

  // Modal
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: 20,
  },
  modal: {
    background: "#fff", borderRadius: 14, padding: 26,
    width: "100%", maxWidth: 500,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
  },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#222", margin: 0, marginBottom: 4 },
  modalSub:   { fontSize: 13, color: "#888", marginBottom: 18 },
  label:      { fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 },
  select:     {
    width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 13, background: "#fff", outline: "none",
    marginBottom: 14, color: "#222",
  },
  textarea:   {
    width: "100%", padding: "9px 12px", border: "1.5px solid #e0d6dc",
    borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none",
    minHeight: 80, marginBottom: 14, resize: "vertical", color: "#222",
    boxSizing: "border-box",
  },
  btnRow: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 },
  btnCancel: {
    background: "#fff", color: "#666", border: "1.5px solid #ddd",
    padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: "pointer",
  },
  btnConfirm: {
    background: "#9f0059", color: "#fff", border: "none",
    padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: "pointer",
  },
  btnFabrica: {
    background: "#1a5fa8", color: "#fff", border: "none",
    padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
    cursor: "pointer",
  },
};

// ─── Modal: Nueva Solicitud ──────────────────────────────────────────────────
const ModalNuevaSolicitud = ({ clients, providers, onSave, onCancel }) => {
  const [clienteId,    setClienteId]    = useState("");
  const [contactos,    setContactos]    = useState([]);
  const [contactoId,   setContactoId]   = useState("");
  const [proveedorId,  setProveedorId]  = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [submitting,   setSubmitting]   = useState(false);

  useEffect(() => {
    if (!clienteId) { setContactos([]); setContactoId(""); return; }
    axios.post(`${BASE}/crm_contactos_listar.php`, new URLSearchParams({ cliente_id: clienteId }))
      .then((r) => setContactos(Array.isArray(r.data) ? r.data : []))
      .catch(() => setContactos([]));
  }, [clienteId]);

  const handleSubmit = async () => {
    if (submitting) return;
    if (!clienteId) { toast.error("Seleccioná un cliente."); return; }
    setSubmitting(true);
    try {
      await onSave({ clienteId, contactoId, proveedorId, observaciones });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <p style={S.modalTitle}>Nueva Solicitud de Cotización</p>
        <p style={S.modalSub}>Anotá la solicitud — después se le agregan productos al aceptar.</p>

        <label style={S.label}>Cliente *</label>
        <select style={S.select} value={clienteId} onChange={(e) => setClienteId(e.target.value)} autoFocus>
          <option value="">Seleccionar cliente…</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.tercero}</option>
          ))}
        </select>

        <label style={S.label}>Contacto</label>
        <select style={S.select} value={contactoId} onChange={(e) => setContactoId(e.target.value)} disabled={!clienteId}>
          <option value="">{clienteId ? "Seleccionar contacto…" : "Elegí un cliente primero"}</option>
          {contactos.map((c) => (
            <option key={c.id} value={c.id}>{c.nombre}{c.cargo ? ` — ${c.cargo}` : ""}</option>
          ))}
        </select>

        <label style={S.label}>Proveedor</label>
        <select style={S.select} value={proveedorId} onChange={(e) => setProveedorId(e.target.value)}>
          <option value="">(opcional) seleccionar proveedor…</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>{p.tercero}</option>
          ))}
        </select>

        <label style={S.label}>Observaciones</label>
        <textarea
          style={S.textarea}
          placeholder="Qué pidió el cliente, plazo aproximado, etc."
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />

        <div style={S.btnRow}>
          <button style={{ ...S.btnCancel, opacity: submitting ? 0.5 : 1 }} onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button style={{ ...S.btnConfirm, opacity: submitting ? 0.7 : 1 }} onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Guardando…" : "Guardar solicitud"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Enviar a Fábrica ─────────────────────────────────────────────────
const ModalEnvioFabrica = ({ solicitud, onConfirm, onCancel }) => {
  const [nota,       setNota]       = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await onConfirm(nota);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <p style={S.modalTitle}>Cotización solicitada al proveedor</p>
        <p style={S.modalSub}>
          {solicitud.cliente}{solicitud.proveedor ? ` — ${solicitud.proveedor}` : ""}
        </p>
        <label style={S.label}>Nota (opcional)</label>
        <textarea
          style={S.textarea}
          placeholder="Ej: cotización solicitada por mail a Juan Pérez, esperamos respuesta en 3 días…"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          autoFocus
        />
        <div style={S.btnRow}>
          <button style={{ ...S.btnCancel, opacity: submitting ? 0.5 : 1 }} onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button style={{ ...S.btnFabrica, opacity: submitting ? 0.7 : 1 }} onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Guardando…" : "Confirmar envío"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Rechazar Solicitud ───────────────────────────────────────────────
const ModalRechazo = ({ solicitud, onConfirm, onCancel }) => {
  const [motivo,     setMotivo]     = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (submitting) return;
    if (!motivo.trim()) { toast.error("El motivo es obligatorio."); return; }
    setSubmitting(true);
    try {
      await onConfirm(motivo);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <p style={S.modalTitle}>Rechazar solicitud</p>
        <p style={S.modalSub}>{solicitud.cliente} {solicitud.proveedor ? `— ${solicitud.proveedor}` : ""}</p>
        <label style={S.label}>Motivo del rechazo *</label>
        <textarea
          style={S.textarea}
          placeholder="Explicá por qué se rechaza esta solicitud..."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
        />
        <div style={S.btnRow}>
          <button style={{ ...S.btnCancel, opacity: submitting ? 0.5 : 1 }} onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button style={{ ...S.btnConfirm, opacity: submitting ? 0.7 : 1 }} onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Procesando…" : "Confirmar rechazo"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Modal: Reporte de seguimiento para proveedor ────────────────────────────
const ModalReporteProveedor = ({ solicitudesEnFabrica, providers, userId, userMail, onClose }) => {
  const [proveedorId,  setProveedorId]  = useState("");
  const [copiado,      setCopiado]      = useState(false);
  const [modoMail,     setModoMail]     = useState(false);
  const [mailTo,       setMailTo]       = useState("");
  const [mailSubject,  setMailSubject]  = useState("");
  const [mailBody,     setMailBody]     = useState("");
  const [enviando,     setEnviando]     = useState(false);

  // Armar lista de proveedores únicos con solicitudes en espera
  const proveedoresDisponibles = (() => {
    const map = {};
    solicitudesEnFabrica.forEach((s) => {
      if (s.proveedor_id && s.proveedor) map[s.proveedor_id] = s.proveedor;
    });
    return Object.entries(map).map(([id, nombre]) => {
      const full = providers.find((p) => String(p.id) === String(id));
      return { id, nombre, mail: full?.mail || "" };
    });
  })();

  // Solicitudes del proveedor seleccionado
  const items = proveedorId
    ? solicitudesEnFabrica.filter((s) => String(s.proveedor_id) === String(proveedorId))
    : [];

  // Calcular días desde envío al proveedor
  const diasDesdeEnvio = (fechaEnvio) => {
    if (!fechaEnvio) return null;
    const diff = Math.floor((Date.now() - new Date(fechaEnvio).getTime()) / 86400000);
    return diff;
  };

  // Texto del reporte para copiar
  const textoReporte = (() => {
    if (!items.length) return "";
    const proveedor = proveedoresDisponibles.find((p) => String(p.id) === String(proveedorId))?.nombre || "";
    const hoy = new Date().toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
    const lineas = items.map((s) => {
      const dias = diasDesdeEnvio(s.fecha_enviada_fabrica);
      const diasStr = dias != null ? `${dias} día${dias !== 1 ? "s" : ""} desde el envío` : "fecha no registrada";
      const obs = s.observaciones ? ` — ${s.observaciones}` : "";
      return `• ${s.cliente} (${diasStr})${obs}`;
    });
    return `Seguimiento de cotizaciones — ${proveedor}\n${hoy}\n\n${lineas.join("\n")}`;
  })();

  const handleCopiar = () => {
    navigator.clipboard.writeText(textoReporte).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    });
  };

  const proveedorActual = proveedoresDisponibles.find((p) => String(p.id) === String(proveedorId));

  const abrirCompose = () => {
    setMailTo(proveedorActual?.mail || "");
    setMailSubject(`Seguimiento cotizaciones — ${proveedorActual?.nombre || ""}`);
    setMailBody(textoReporte);
    setModoMail(true);
  };

  const handleEnviarMail = async () => {
    if (!mailTo.trim()) { toast.error("El email del proveedor es requerido."); return; }
    if (enviando) return;
    setEnviando(true);
    try {
      const fd = new FormData();
      fd.append("to_email", mailTo.trim());
      fd.append("subject",  mailSubject.trim());
      fd.append("body",     mailBody.trim());
      fd.append("user_id",  userId);
      const res = await axios.post(`${BASE}/enviar_seguimiento_proveedor.php`, fd);
      if (res.data.success) {
        toast.success(`✓ Mail enviado a ${mailTo}`);
        onClose();
      } else {
        toast.error(res.data.mensaje || "Error al enviar el mail");
      }
    } catch {
      toast.error("Error de conexión");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div style={S.overlay}>
      <div style={{ ...S.modal, maxWidth: 580 }}>

        {/* ── Vista: seleccionar proveedor y ver reporte ── */}
        {!modoMail && (
          <>
            <p style={S.modalTitle}>Reporte de seguimiento</p>
            <p style={S.modalSub}>Cotizaciones pendientes de respuesta por proveedor</p>

            <label style={S.label}>Proveedor</label>
            <select style={S.select} value={proveedorId} onChange={(e) => { setProveedorId(e.target.value); setCopiado(false); }} autoFocus>
              <option value="">Seleccionar proveedor…</option>
              {proveedoresDisponibles.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>

            {proveedorId && (
              items.length === 0 ? (
                <p style={{ color: "#aaa", fontSize: 13, margin: "8px 0 16px" }}>
                  Este proveedor no tiene solicitudes en espera.
                </p>
              ) : (
                <div style={{
                  background: "#f8f6fa", border: "1.5px solid #ede8ed", borderRadius: 8,
                  padding: "14px 16px", marginBottom: 14, fontSize: 13, lineHeight: 1.7,
                  whiteSpace: "pre-wrap", fontFamily: "inherit", color: "#333",
                  maxHeight: 240, overflowY: "auto",
                }}>
                  {textoReporte}
                </div>
              )
            )}

            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={onClose}>Cerrar</button>
              {items.length > 0 && (
                <>
                  <button
                    style={{ ...S.btnConfirm, background: copiado ? "#0a6e3f" : "#9f0059", minWidth: 120 }}
                    onClick={handleCopiar}
                  >
                    {copiado ? "✓ Copiado" : "Copiar texto"}
                  </button>
                  <button
                    style={{ ...S.btnFabrica, minWidth: 130 }}
                    onClick={abrirCompose}
                  >
                    ✉ Enviar por mail
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Vista: compose mail ── */}
        {modoMail && (
          <>
            <p style={S.modalTitle}>Enviar por mail</p>
            <p style={S.modalSub}>
              De: <strong>{userMail || "tu casilla"}</strong> (las respuestas llegan a tu correo)
            </p>

            <label style={S.label}>Para</label>
            {proveedorActual && !proveedorActual.mail ? (
              <p style={{ fontSize: 12, color: "#e07a00", background: "#fff8e1", border: "1px solid #fcd34d",
                borderRadius: 7, padding: "8px 12px", marginBottom: 14 }}>
                ⚠️ Este proveedor no tiene email configurado.{" "}
                <a href="/sistema/#/sistema/dashboard/admin/suppliers" target="_blank"
                  style={{ color: "#1a5fa8", fontWeight: 600 }}>
                  Agregar en Proveedores →
                </a>
              </p>
            ) : null}
            <input
              type="email"
              style={{ ...S.select, marginBottom: 14 }}
              value={mailTo}
              onChange={(e) => setMailTo(e.target.value)}
              placeholder="email@proveedor.com"
            />

            <label style={S.label}>Asunto</label>
            <input
              type="text"
              style={{ ...S.select, marginBottom: 14 }}
              value={mailSubject}
              onChange={(e) => setMailSubject(e.target.value)}
            />

            <label style={S.label}>Mensaje</label>
            <textarea
              style={{ ...S.textarea, minHeight: 200 }}
              value={mailBody}
              onChange={(e) => setMailBody(e.target.value)}
            />

            <div style={S.btnRow}>
              <button style={S.btnCancel} onClick={() => setModoMail(false)}>← Volver</button>
              <button style={S.btnCancel} onClick={onClose}>Cancelar</button>
              <button
                style={{ ...S.btnFabrica, opacity: enviando ? 0.7 : 1, minWidth: 120 }}
                onClick={handleEnviarMail}
                disabled={enviando}
              >
                {enviando ? "Enviando…" : "✉ Enviar mail"}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────
const SolicitudesAdmin = () => {
  const [solicitudes,         setSolicitudes]         = useState([]);
  const [clients,             setClients]             = useState([]);
  const [providers,           setProviders]           = useState([]);
  const [modalNueva,          setModalNueva]          = useState(false);
  const [modalReporte,        setModalReporte]        = useState(false);
  const [solicitudARechazar,  setSolicitudARechazar]  = useState(null);
  const [solicitudAFabrica,   setSolicitudAFabrica]   = useState(null);
  const history    = useHistory();
  const isMounted  = useRef(false);
  const { user }   = useContext(UserContext);

  const traerSolicitudes = () => {
    axios.post(`${BASE}/solicitudes_listar.php`)
      .then((r) => {
        if (Array.isArray(r.data) && isMounted.current) setSolicitudes(r.data);
      })
      .catch(() => toast.error("Error al cargar solicitudes."));
  };

  useEffect(() => {
    isMounted.current = true;
    traerSolicitudes();
    axios.post(`${BASE}/buscar_terceros.php`).then((r) => {
      setClients(r.data.filter((x) => x.tipo_other === 2));
      setProviders(r.data.filter((x) => x.tipo_other === 3));
    }).catch(() => {});
    return () => { isMounted.current = false; };
  }, []);

  const handleGuardarNueva = async ({ clienteId, contactoId, proveedorId, observaciones }) => {
    try {
      const fd = new FormData();
      fd.append("cliente_id",    clienteId);
      fd.append("contacto_id",   contactoId || "");
      fd.append("proveedor_id",  proveedorId || "");
      fd.append("observaciones", observaciones || "");
      fd.append("user_id",       user?.id ?? 0);
      const res = await axios.post(`${BASE}/solicitudes_guardar.php`, fd);
      if (res.data.success) {
        toast.success("Solicitud guardada");
        setModalNueva(false);
        traerSolicitudes();
      } else {
        toast.error(res.data.mensaje || "Error al guardar");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const handleAceptar = (s) => {
    history.push(`/dashboard/newquotation?solicitud=${s.id}`);
  };

  const handleConfirmarEnvioFabrica = async (nota) => {
    try {
      const fd = new FormData();
      fd.append("id",      solicitudAFabrica.id);
      fd.append("nota",    nota || "");
      fd.append("user_id", user?.id ?? 0);
      const res = await axios.post(`${BASE}/solicitudes_enviar_fabrica.php`, fd);
      if (res.data.success) {
        toast.success("Cotización solicitada al proveedor");
        setSolicitudAFabrica(null);
        traerSolicitudes();
      } else {
        toast.error(res.data.mensaje || "Error al actualizar");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  const handleConfirmarRechazo = async (motivo) => {
    try {
      const fd = new FormData();
      fd.append("id",     solicitudARechazar.id);
      fd.append("motivo", motivo);
      const res = await axios.post(`${BASE}/solicitudes_rechazar.php`, fd);
      if (res.data.success) {
        toast.success("Solicitud rechazada");
        setSolicitudARechazar(null);
        traerSolicitudes();
      } else {
        toast.error(res.data.mensaje || "Error al rechazar");
      }
    } catch {
      toast.error("Error de conexión");
    }
  };

  // ── Desglose para el subtítulo ─────────────────────────────────────────────
  const nPendientes = solicitudes.filter((s) => s.estado === "pendiente").length;
  const nFabrica    = solicitudes.filter((s) => s.estado === "en_fabrica").length;
  const subtitulo   = [
    nPendientes > 0 ? `${nPendientes} pendiente${nPendientes !== 1 ? "s" : ""}` : null,
    nFabrica    > 0 ? `${nFabrica} esperando proveedor`                          : null,
  ].filter(Boolean).join(" · ") || "Sin solicitudes activas";

  return (
    <section style={S.page}>
      <CotizacionesNav />

      <div style={S.header}>
        <div>
          <h2 style={S.title}>Solicitudes de cotizaciones</h2>
          <p style={S.subtitle}>{subtitulo}</p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          {nFabrica > 0 && (
            <button
              style={{ ...S.btnPrimary, background: "#fff", color: "#1a5fa8", border: "1.5px solid #1a5fa840" }}
              onClick={() => setModalReporte(true)}
            >
              📋 Seguimiento proveedor
            </button>
          )}
          <button style={S.btnPrimary} onClick={() => setModalNueva(true)}>
            + Nueva Solicitud de Cotización
          </button>
        </div>
      </div>

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              {["Fecha", "Días", "Etapa", "Cliente", "Contacto", "Proveedor", "Observaciones", "Acciones"].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {solicitudes.length === 0 ? (
              <tr><td colSpan={8} style={S.emptyRow}>No hay solicitudes activas 🎉</td></tr>
            ) : (
              solicitudes.map((s, i) => {
                const dias      = s.dias_pendiente != null ? Number(s.dias_pendiente) : null;
                const enFabrica = s.estado === "en_fabrica";

                // Tooltip para el chip "En fábrica"
                const tooltipFabrica = enFabrica
                  ? [
                      `Enviado el ${formatFecha(s.fecha_enviada_fabrica)}`,
                      s.usuario_fabrica ? `por ${s.usuario_fabrica}` : null,
                      s.nota_enviada_fabrica ? `\n"${s.nota_enviada_fabrica}"` : null,
                    ].filter(Boolean).join(" ")
                  : undefined;

                return (
                  <tr key={s.id} style={i % 2 === 0 ? S.trOdd : S.trEven}>
                    <td style={S.td}>{formatFecha(s.fecha_solicitud)}</td>
                    <td style={S.td}>
                      <span style={S.diasBadge(dias)}>
                        {dias == null ? "—" : `${dias}d`}
                      </span>
                    </td>
                    <td style={S.td}>
                      <span style={S.etapaChip(s.estado)} title={tooltipFabrica}>
                        {enFabrica ? "🏭 Esperando proveedor" : "Pendiente"}
                      </span>
                    </td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{s.cliente || "—"}</td>
                    <td style={S.td}>{s.contacto || "—"}</td>
                    <td style={S.td}>{s.proveedor || "—"}</td>
                    <td style={{ ...S.td, color: "#666", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.observaciones || "—"}
                    </td>
                    <td style={S.td}>
                      <div style={S.accionesCel}>
                        {/* Botón "Enviar a fábrica" — solo desde estado pendiente */}
                        {!enFabrica && (
                          <button
                            style={{ background: "none", border: "1.5px solid #1a5fa830", color: "#1a5fa8",
                              cursor: "pointer", padding: "6px 8px", borderRadius: 7,
                              display: "flex", alignItems: "center", fontSize: 15, transition: "all 0.15s" }}
                            title="Marcar cotización como solicitada al proveedor"
                            onClick={() => setSolicitudAFabrica(s)}
                          >
                            🏭
                          </button>
                        )}
                        {/* Botón Aceptar / Cotización recibida */}
                        <button
                          style={{ background: "none", border: "1.5px solid #0a6e3f30", color: "#0a6e3f",
                            cursor: "pointer", padding: 6, borderRadius: 7,
                            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
                          title={enFabrica ? "Cotización recibida — crear cotización formal" : "Aceptar y crear cotización"}
                          onClick={() => handleAceptar(s)}
                        >
                          <MdCheck size={20} />
                        </button>
                        {/* Botón Rechazar */}
                        <button
                          style={{ background: "none", border: "1.5px solid #9f005930", color: "#9f0059",
                            cursor: "pointer", padding: 6, borderRadius: 7,
                            display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
                          title="Rechazar"
                          onClick={() => setSolicitudARechazar(s)}
                        >
                          <MdClose size={20} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {modalNueva && (
        <ModalNuevaSolicitud
          clients={clients}
          providers={providers}
          onSave={handleGuardarNueva}
          onCancel={() => setModalNueva(false)}
        />
      )}

      {solicitudAFabrica && (
        <ModalEnvioFabrica
          solicitud={solicitudAFabrica}
          onConfirm={handleConfirmarEnvioFabrica}
          onCancel={() => setSolicitudAFabrica(null)}
        />
      )}

      {solicitudARechazar && (
        <ModalRechazo
          solicitud={solicitudARechazar}
          onConfirm={handleConfirmarRechazo}
          onCancel={() => setSolicitudARechazar(null)}
        />
      )}

      {modalReporte && (
        <ModalReporteProveedor
          solicitudesEnFabrica={solicitudes.filter((s) => s.estado === "en_fabrica")}
          providers={providers}
          userId={user?.id ?? 0}
          userMail={user?.mail ?? user?.datos?.mail ?? ""}
          onClose={() => setModalReporte(false)}
        />
      )}
    </section>
  );
};

export default SolicitudesAdmin;
